/**
 * @file 套餐切换确认弹窗
 */
import React from 'react';
import styles from './PlanConfirmModal.module.less';
import {useMembershipQuestions} from './hooks/useMemberShip';
import {getPrice} from './utils/priceHelper';
import {fenToYuan} from '@/utils/money';
import {formatUnixDate} from '@/utils/date';

import Modal from '@/components/common/Modal';
import Button from '@/components/common/Button';
import Icon from '@/components/common/Icon';
import {createCx} from '@/utils/cx';
import {OrderTypeValue, type MemberLevelItem} from '@/services/commerce/membership/interface';

const cx = createCx(styles);

/**
 * 明细项类型
 */
interface DetailItem {
    label: string;
    value: string | number;
    highlight?: boolean; // 是否高亮显示（如价格、差价等）
}

/**
 * 明细区块类型
 */
interface DetailSection {
    icon?: React.ReactNode;
    title: string;
    list: DetailItem[];
}

/**
 * 明细项组件
 */
const DetailItemComp: React.FC<{item: DetailItem}> = ({item}) => {
    return (
        <div>
            {item.value && (
                <div className={cx('detailItem')}>
                    <span className={cx('detailLabel')}>{item.label}</span>
                    <span className={cx('detailValue', {highlight: item.highlight})}>
                        {item.value}
                    </span>
                </div>
            )}
        </div>
    );
};

/**
 * 明细区块组件
 */
const DetailSectionComp: React.FC<{section: DetailSection}> = ({section}) => {
    return (
        <div className={cx('detailSection')}>
            <div className={cx('sectionHeader')}>
                {section.icon && <span className={cx('sectionIcon')}>{section.icon}</span>}
                <span className={cx('sectionTitle')}>{section.title}</span>
            </div>
            <div className={cx('detailList')}>
                {section.list.map((item) => (
                    <DetailItemComp key={`${section.title}-${item.label}`} item={item} />
                ))}
            </div>
        </div>
    );
};

/**
 * 明细卡组件
 * 用于展示费用明细、权益明细等信息
 */
interface DetailCardProps {
    sections: DetailSection[];
}

const DetailCard: React.FC<DetailCardProps> = ({sections}) => {
    return (
        <div className={cx('detailCard')}>
            {sections.map((section) => (
                <DetailSectionComp key={section.title} section={section} />
            ))}
        </div>
    );
};

/**
 * 构建费用明细数据
 */
function buildFeeSection(plan: MemberLevelItem, isUpgrade: boolean): DetailSection {
    const items = [
        {
            label: '原会员支付金额',
            value: isUpgrade ? `¥${fenToYuan(plan.upgradeInfo?.originPrice ?? 0)} ` : '',
        },
        {
            label: '剩余权益时长',
            value: isUpgrade ? `${plan.upgradeInfo?.remainingDuration} 天` : '',
        },
        {
            label: '目标会员价',
            value: `¥${fenToYuan(plan.sku.sellPrice)}`,
        },
        {
            label: '应付差价',
            value: isUpgrade ? `¥${fenToYuan(plan.upgradeInfo?.priceDiff ?? 0)}` : '',
            highlight: true,
        },
    ];
    return {
        icon: <Icon name="AijBasicPingtaixieyi" size={14} />,
        title: '费用明细',
        list: items.filter((item) => item.value !== ''),
    };
}

/**
 * 构建权益明细数据
 */
function buildBenefitSection(plan: MemberLevelItem, isUpgrade: boolean): DetailSection {
    const items = [
        {
            label: '原会员发放积分',
            value: isUpgrade ? `${plan.upgradeInfo?.originPoint} 积分` : '',
        },
        {
            label: '目标会员发放积分',
            value: isUpgrade ? `${plan.sku.pointPerMonth} 积分` : '',
        },
        {
            label: '当前积分帐期剩余天数',
            value: isUpgrade ? `${plan.upgradeInfo?.remainingDuration} 天` : '',
        },
        {
            label: '实际到账积分',
            value: isUpgrade
                ? `+${plan.upgradeInfo?.pointDiff} 积分`
                : `${plan.sku.pointPerMonth} 积分`,
            highlight: true,
        },
    ];
    return {
        icon: <Icon name="AijBasicJineng" size={14} />,
        title: '权益明细',
        list: items.filter((item) => item.value !== ''),
    };
}

const formate = 'YYYY年MM月DD日';

/**
 * 构建生效时间明细数据
 */
function buildTimeSection(plan: MemberLevelItem, isUpgrade: boolean): DetailSection {
    return {
        icon: <Icon name="AijBasicRili" size={14} />,
        title: '生效时间',
        list: [
            {
                label: '生效时间',
                value: isUpgrade
                    ? '立即生效'
                    : formatUnixDate(plan.subscriptionInfo?.startTime ?? 0, formate),
            },
            {
                label: '有效期',
                value: `${formatUnixDate((isUpgrade ? plan.upgradeInfo?.startTime : plan.subscriptionInfo?.startTime) ?? 0, formate)}至${formatUnixDate((isUpgrade ? plan.upgradeInfo?.endTime : plan.subscriptionInfo?.endTime) ?? 0, formate)}`,
            },
        ],
    };
}

interface PlanConfirmModalProps {
    open: boolean;
    level: MemberLevelItem;
    subscriptionName: string;
    onConfirm: () => void;
    onClose: () => void;
}

export const PlanConfirmModal: React.FC<PlanConfirmModalProps> = ({
    open,
    level,
    subscriptionName,
    onConfirm,
    onClose,
}) => {
    const {data: questionData} = useMembershipQuestions();
    const upgradeFeeTip = questionData?.upgradeFeeTip;
    const downgradeFeeTip = questionData?.downgradeFeeTip;

    const orderType = level.orderType;
    const isUpgrade = orderType === OrderTypeValue.UPGRADE;

    // 费用明细
    const feeSection = buildFeeSection(level, isUpgrade);
    // 权益明细
    const benefitSection = buildBenefitSection(level, isUpgrade);
    // 生效周期
    const timeSection = buildTimeSection(level, isUpgrade);
    const detailSections: DetailSection[] = [feeSection, benefitSection, timeSection];

    // 费用说明
    const feeTips = isUpgrade ? upgradeFeeTip : downgradeFeeTip;
    // 当前应付金额
    const price = getPrice(level, isUpgrade);

    return (
        <Modal
            open={open}
            onCancel={onClose}
            title={
                <div className={cx('title')}>
                    <span>更换至</span>
                    <span className={cx('levelName')}>
                        {level.levelName} · {subscriptionName}
                    </span>
                </div>
            }
            width={440}
            footer={null}
            className={styles.modal}
        >
            <div className={styles.content}>
                <div className={cx('scrollContent')}>
                    <div className={cx('header')}>
                        应付金额
                        <span className={cx('symbol')}>¥</span>
                        <span className={cx('price')}>{fenToYuan(price)}</span>
                    </div>

                    <DetailCard sections={detailSections} />

                    <div className={cx('notice')}>
                        <div className={cx('noticeTitle')}>费用说明</div>
                        <ul className={cx('noticeContent')}>
                            {feeTips?.map((item: string) => (
                                <li key={item} className={cx('noticeItem')}>
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className={cx('actions')}>
                    <Button type="primary" className={cx('confirmBtn')} onClick={onConfirm}>
                        确认支付 ¥{fenToYuan(price)}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default PlanConfirmModal;
